import React, { useEffect, useState } from "react";
import {
    Table,
    Button,
    Form,
    Input,
    Space,
    message,
    Switch,
    Skeleton,
    Typography,
    notification,
    Tooltip,
    Popconfirm,
    Segmented,
    Select,
    Tag
} from "antd";
import { EditOutlined, PlusOutlined } from "@ant-design/icons";
import {
    createForcedUpsell,
    updateForcedUpsell,
    updateForcedStatus,
    getUpsellById,
    searchForcedUpsells
} from "../../services/upsellservice";
import { ForcedUpsellDetailDto, ForcedUpsellDto } from "../../types/upselltypes";
import { renderBadgeSwatch, getBadgeColorName } from "../../components/upsells/upsellbadgeutils";
import { useAuth } from "../../auth/authcontext";
import { getAllStoresForDropdown } from "../../services/storeserivce";
import ForcedUpsellDrawer from "../../components/upsells/forcedupselldrawer";
import { StoreDropdownDto } from "../../types/storetypes";

const { Title, Text } = Typography;
const { Search } = Input;

type StatusFilter = "all" | "active" | "inactive";

const ForcedUpsells: React.FC = () => {

    const [upsells, setUpsells] = useState<ForcedUpsellDto[]>([]);
    const [loading, setLoading] = useState(false);

    const [drawerOpen, setDrawerOpen] = useState(false);
    const [editing, setEditing] = useState<ForcedUpsellDetailDto | null>(null);

    const [form] = Form.useForm();

    const { userId, isReadOnly } = useAuth();
    const createdByGuid = userId || "";

    const [stores, setStores] = useState<any[]>([]);
    const [selectedStore, setSelectedStore] = useState<StoreDropdownDto | null>(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");

    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0
    });
    const [loadingEdit, setLoadingEdit] = useState(false);

    const showError = (msg: string) => {
        notification.error({
            message: "Error",
            description: msg,
            placement: "topRight"
        });
    };

    useEffect(() => {
        getAllStoresForDropdown()
            .then(res => {
                const mappedStores = res.data.map((s: any) => ({
                    storeGuid: s.guid,
                    storeNumber: s.storeNumber
                }));
                setStores(mappedStores);
            })
            .catch(() => showError("Failed to load stores"));
    }, []);

    const fetchUpsells = async (
        page = 1,
        pageSize = pagination.pageSize,
        search = searchTerm,
        status = statusFilter,
        selectedStoreNumber = selectedStore?.storeNumber
    ) => {
        if (!selectedStoreNumber) return;

        setLoading(true);
        try {

            const res = await searchForcedUpsells({
                storeNumber: selectedStoreNumber,
                pageNumber: page,
                pageSize: pageSize,
                search: search,
                active: status === "all" ? null : status === "active"
            });

            setUpsells(res.data);

            setPagination({
                current: page,
                pageSize,
                total: res.totalCount
            });

        } catch {
            showError("Failed to load upsells");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (selectedStore?.storeNumber) {
            fetchUpsells(1, pagination.pageSize, "", statusFilter, selectedStore.storeNumber);
        }
    }, [selectedStore]);

    useEffect(() => {
        if (selectedStore?.storeNumber) {
            fetchUpsells(1, pagination.pageSize, searchTerm, statusFilter, selectedStore.storeNumber);
        }
    }, [statusFilter]);

    const handleSearch = (value: string) => {
        const trimmed = value.trim();
        setSearchTerm(trimmed);

        fetchUpsells(
            1,
            pagination.pageSize,
            trimmed,
            statusFilter,
            selectedStore?.storeNumber
        );
    };

    const handleSearchChange = (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        if (!e.target.value) {
            setSearchTerm("");
            fetchUpsells(
                1,
                pagination.pageSize,
                "",
                statusFilter,
                selectedStore?.storeNumber
            );
        }
    };

    const handleTableChange = (p: any) => {
        fetchUpsells(
            p.current,
            p.pageSize,
            searchTerm,
            statusFilter,
            selectedStore?.storeNumber
        );
    };

    const openCreateDrawer = () => {

        if (!selectedStore?.storeGuid) {
            showError("Please select a valid store first");
            return;
        }

        setEditing(null);
        form.resetFields();
        setDrawerOpen(true);
    };

    const openEditDrawer = async (record: ForcedUpsellDto) => {
        try {
            setLoadingEdit(true);
            setDrawerOpen(true);

            const data = await getUpsellById(record.guid);

            setEditing(data);

            form.setFieldsValue({
                ...data,
                dayParts: data.dayParts ?? []
            });
        } catch {
            showError("Failed to load upsell details");
        }
        finally {
            setLoadingEdit(false);
        }

    };

    const onSave = async () => {
        const values = await form.validateFields();

        const payload = {
            ...values,
            ageRestricted:
                values.ageRestricted === "" || values.ageRestricted === undefined
                    ? null
                    : values.ageRestricted,
            storeNumber: selectedStore?.storeNumber,
            storeGuid: selectedStore?.storeGuid
        };

        try {
            if (editing) {
                await updateForcedUpsell(editing.guid, payload);
                message.success("Upsell updated");
            } else {
                await createForcedUpsell({
                    ...payload,
                    createdBy: createdByGuid
                });
                message.success("Upsell created");
            }

            setDrawerOpen(false);
            form.resetFields();

            fetchUpsells(
                pagination.current,
                pagination.pageSize,
                searchTerm,
                statusFilter,
                selectedStore?.storeNumber
            );

        } catch {
            showError("Failed to save upsell");
        }
    };

    const toggleStatus = async (guid: string, active: boolean) => {
        try {
            await updateForcedStatus(guid, active);
            message.success("Status updated");

            fetchUpsells(
                pagination.current,
                pagination.pageSize,
                searchTerm,
                statusFilter,
                selectedStore?.storeNumber
            );

        } catch {
            showError("Failed to update status");
        }
    };

    const columns = [
        { title: "Item Id", dataIndex: "itemId" },
        { title: "Source", dataIndex: "source" },
        {
            title: "Display Name",
            render: (r: ForcedUpsellDto) =>
                r.displayName ? r.displayName : <Tag color="orange">Not Set</Tag>
        },
        {
            title: "Badge",
            render: (r: ForcedUpsellDto) => r.badgeText || <Tag color="orange">Not Set</Tag>
        },
        {
            title: "Badge Style",
            render: (r: ForcedUpsellDto) => (
                r.badgeStyle ? (
                    <Space>
                        {renderBadgeSwatch(r.badgeStyle)}
                        <span>{getBadgeColorName(r.badgeStyle)}</span>
                    </Space>
                ) : (
                    <Tag color="orange">Not Set</Tag>
                )
            )
        },
        {
            title: "Day Parts",
            render: (r: ForcedUpsellDto) =>
                r.dayParts?.length ? r.dayParts.join(", ") : <Tag color="orange">Not Set</Tag>
        },
        {
            title: "Reason",
            render: (r: ForcedUpsellDto) => r.reason || <Tag color="orange">Not Set</Tag>
        },
        {
            title: "Active",
            dataIndex: "active",
            render: (value: boolean, record: ForcedUpsellDto) => (
                <Popconfirm
                    title={value ? "Disable upsell" : "Enable upsell"}
                    onConfirm={() => toggleStatus(record.guid, !value)}
                    disabled={isReadOnly}
                >
                    <Switch
                        checked={value}
                        size="small"
                        onClick={(_, e) => e.preventDefault()}
                        disabled={isReadOnly}
                    />
                </Popconfirm>
            )
        },
        {
            title: "Actions",
            render: (_: any, record: ForcedUpsellDto) => (
                <Tooltip title="Edit">
                    <Button
                        icon={<EditOutlined />}
                        onClick={() => openEditDrawer(record)}
                    />
                </Tooltip>
            )
        }
    ];

    const handleClose = () => {
        form.resetFields();
        setEditing(null);
        setDrawerOpen(false);
    };


    return (
        <div style={{ padding: 24 }}>
            <Title level={2}>Forced Upsells</Title>

            <Space style={{ marginBottom: 16 }} wrap>
                <Select
                    placeholder="Select Store"
                    allowClear

                    onChange={(value) => {
                        const store = stores.find(s => s.storeNumber === value);

                        setSelectedStore(store || null);
                    }}

                    style={{ width: 220 }}
                    showSearch
                    optionFilterProp="children"
                >
                    {stores.map((s) => (
                        <Select.Option key={s.storeNumber} value={s.storeNumber}>
                            {s.storeNumber}
                        </Select.Option>
                    ))}
                </Select>

                <Search
                    placeholder="Search by name or Id"
                    allowClear
                    style={{ maxWidth: 300 }}
                    onSearch={handleSearch}
                    onChange={handleSearchChange}
                />

                <Segmented
                    value={statusFilter}
                    onChange={(val) => setStatusFilter(val as StatusFilter)}
                    options={[
                        { label: "All", value: "all" },
                        { label: "Active", value: "active" },
                        { label: "Inactive", value: "inactive" }
                    ]}
                />

                <Button
                    color="primary"
                    variant="filled"
                    icon={<PlusOutlined />}
                    onClick={openCreateDrawer}
                    disabled={!selectedStore?.storeNumber}
                >
                    Add Forced Upsell
                </Button>
            </Space>

            {!selectedStore?.storeNumber ? (
                <Text type="secondary" style={{ marginLeft: '12px' }}>
                    Please select a store to view upsells
                </Text>
            ) : loading ? (
                <Skeleton active paragraph={{ rows: 6 }} />
            ) : (
                <Table
                    rowKey="guid"
                    columns={columns}
                    dataSource={upsells}
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: pagination.total,
                        showSizeChanger: true,
                        pageSizeOptions: ["5", "10", "20", "50"],
                        showTotal: (total, range) =>
                            `Showing ${range[0]}–${range[1]} of ${total} upsells`
                    }}
                    onChange={handleTableChange}
                />
            )}

            {drawerOpen && selectedStore && (
                <ForcedUpsellDrawer
                    open={drawerOpen}
                    editing={editing}
                    form={form}
                    onClose={handleClose}
                    onSave={onSave}
                    storeGuid={selectedStore.storeGuid}
                    loadingForm={loadingEdit}
                />
            )}

        </div>
    );
};

export default ForcedUpsells;